import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { WarningAmber } from '@mui/icons-material';
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import {
  FHAntdDateTimePicker,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHSelect,
} from '../../../../../base';
import { useTreatmentPlanEditable } from '../../../hooks/useTreatmentPlanEditable';
import { goalActions } from '../../../../../../store';
import { getAvailableStages, getTargetStagesForGoalType } from '../utils/general';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY1,
  FORTA_GRAY2,
  FORTA_WHITE,
} from '../../../../../../utils/colors';
import { transformStageHistory } from '../utils/general';
import { disabledFutureDate } from '../../../../../../helpers/date-helpers';
import { StyledTableCell, StageIndicator, StageArrow } from './styles';
import { GoalTypes, TargetStages, TargetStageStatus } from '../../common/constants/general';
import { STATUS_COLORS } from '../constants/general';
import ChangeStageIcon from '../../../../../svgs/ChangeStageIcon';
import TrashIcon3 from '../../../../../svgs/TrashIcon3';

import type {
  ITargetStageChange,
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
} from '../../../../../../utils/types';
import type { RootState } from '../../../../../../store';

function isTargetStage(value: string | number): value is TargetStages {
  return Object.values(TargetStages).some((stage) => stage === value);
}

interface TargetStageChangeProps {
  target: ITreatmentPlanGoalTarget;
  goalId: number;
  goalType: GoalTypes;
  goalActive: boolean;
}

function TargetStageChange(props: TargetStageChangeProps) {
  const { target, goalId, goalType } = props;
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isTpEditable = useTreatmentPlanEditable();

  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals } = useSelector(
    (state: RootState) => state.goals,
  );

  const [targetData, setTargetData] = useState(target);
  const [targetStage, setTargetStage] = useState<TargetStages | null>(null);
  const [targetStageDate, setTargetStageDate] = useState<string | null>(null);
  const [updateStageDates, setUpdateStageDates] = useState<Record<number, string | null>>({});
  const [targetStageReason, setTargetStageReason] = useState<string | null>(null);
  const [showDeleteStageConfirmation, setShowDeleteStageConfirmation] = useState(false);
  const isTargetStageDateValid = useMemo(() => {
    if (!targetData?.stage_change_logs) return true;

    //Edge case after migration: If the target stage is on hold and the date is null, we don't need to validate the date
    if (
      targetData.stage_change_logs.length === 1 &&
      targetData.stage_change_logs[0].new_stage === TargetStages.OnHold &&
      targetData.stage_change_logs[0].date_stage_changed === null
    ) {
      return true;
    }

    return !(
      targetData?.stage_change_logs.length === 1 &&
      targetData?.stage_change_logs[0].date_stage_changed == null
    );
  }, [targetData]);

  useEffect(() => {
    if (
      !targetData ||
      JSON.stringify(target.stage_change_logs) !== JSON.stringify(targetData.stage_change_logs) ||
      target.is_editing !== targetData.is_editing
    ) {
      setTargetData(target);
    }
  }, [target]);

  useEffect(() => {
    resetForm();
  }, [target.id]);

  const currentTarget = useSelector((state: RootState) => {
    const goalListMap = {
      [GoalTypes.BehaviorReduction]: state.goals.behaviorReductionGoals,
      [GoalTypes.SkillAcquisition]: state.goals.skillAcquisitionGoals,
      [GoalTypes.Caregiver]: state.goals.caregiverGoals,
    };

    const currentGoalList = goalListMap[goalType];
    const goal = currentGoalList.find((g) => g.id === goalId);
    return goal?.targets.find((t) => t.id === targetData?.id);
  });

  const allStages = getTargetStagesForGoalType(goalType);
  const stages = allStages.filter(
    (stage) =>
      ![TargetStages.OnHold, TargetStages.Resume, TargetStages.Discontinued].some(
        (excludedStage) => stage.value === excludedStage,
      ),
  );

  const targetStageLog = targetData?.stage_change_logs || [];
  const stageHistory = transformStageHistory(targetStageLog, goalType);

  const updateGoalsList = (updatedTarget: ITreatmentPlanGoalTarget) => {
    const goalListMap = {
      [GoalTypes.BehaviorReduction]: behaviorReductionGoals,
      [GoalTypes.SkillAcquisition]: skillAcquisitionGoals,
      [GoalTypes.Caregiver]: caregiverGoals,
    };

    const currentGoalList = goalListMap[goalType];

    dispatch(
      goalActions.updateGoals({
        type: goalType,
        list: currentGoalList.map((g: ITreatmentPlanGoal) =>
          g.id === goalId
            ? {
                ...g,
                is_active: updatedTarget.is_goal_active,
                is_linkable: updatedTarget.is_goal_linkable,
                targets: g.targets.map((t) =>
                  t.id === currentTarget?.id
                    ? {
                        ...t,
                        is_active: updatedTarget.is_active,
                        is_goal_active: updatedTarget.is_goal_active,
                        reason: updatedTarget.reason,
                        stage_change_logs: updatedTarget.stage_change_logs,
                        is_editing: targetData.is_editing,
                      }
                    : t,
                ),
              }
            : g,
        ),
      }),
    );
  };

  const resetForm = () => {
    setTargetStageDate(null);
    setTargetStage(null);
    setTargetStageReason(null);
  };

  const handleStageChange = async () => {
    try {
      if (!targetData?.id || !targetStageDate || !targetStage || !treatmentPlan) return;

      const stageChangeData: ITargetStageChange = {
        target_id: targetData.id,
        date_stage_changed: targetStageDate,
        stage: targetStage,
        reason_for_change:
          targetStage === TargetStages.OnHold
            ? `On Hold - ${targetStageReason}`
            : targetStage === TargetStages.Discontinued
              ? `Discontinued - ${targetStageReason}`
              : targetStageReason,
      };

      const res = await dispatch(
        goalActions.changeTargetStage({
          treatmentPlanId: treatmentPlan.id,
          goalId,
          targetId: stageChangeData.target_id,
          data: stageChangeData,
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;

      const updatedTargetWithEditing = {
        ...res.payload,
        is_editing: res.payload.is_active === false ? false : targetData.is_editing,
      };

      updateGoalsList(updatedTargetWithEditing);
      setTargetData(updatedTargetWithEditing);
      resetForm();
    } catch (error) {
      console.error('Failed to change stage:', error);
    }
  };

  const handleUpdateTargetStage = async (newDate: string | null) => {
    try {
      if (!newDate || !treatmentPlan) {
        throw new Error('Missing required fields');
      }

      const res = await dispatch(
        goalActions.updateTargetStage({
          treatmentPlanId: treatmentPlan.id,
          goalId,
          targetId: currentTarget?.id,
          data: { date_stage_changed: newDate },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;

      const updatedTargetWithEditing = {
        ...res.payload,
        is_editing: targetData.is_editing,
      };

      updateGoalsList(updatedTargetWithEditing);
      setTargetData(updatedTargetWithEditing);
      setTargetStageDate(null);
    } catch (error) {
      console.error('Failed to update stage:', error);
    }
  };

  const handleDeleteStage = async () => {
    setShowDeleteStageConfirmation(false);
    try {
      if (!treatmentPlan) {
        throw new Error('Treatment plan not found');
      }

      const res = await dispatch(
        goalActions.deleteTargetStage({
          treatmentPlanId: treatmentPlan.id,
          goalId,
          targetId: currentTarget?.id,
          getAccessTokenSilently,
        }),
      );

      if (!res.payload) throw new Error('Failed to delete stage');

      const updatedTargetWithEditing = {
        ...res.payload,
        is_editing: targetData.is_editing,
        stage_change_logs: res.payload.stage_change_logs || [],
      };

      setTargetData(updatedTargetWithEditing);
      updateGoalsList(updatedTargetWithEditing);

      resetForm();
    } catch (error) {
      console.error('Failed to delete stage:', error);
    }
  };

  const handleUpdateDateForTarget = (targetId: number, value: any) => {
    const newDate = value ? moment(value).format('YYYY-MM-DD') : null;
    setUpdateStageDates((prev) => ({
      ...prev,
      [targetId]: newDate,
    }));
    handleUpdateTargetStage(newDate);
  };

  return (
    <Grid item xs={12} my={2}>
      <Box display={'flex-column'} mt={1}>
        <Typography textAlign={'left'} fontWeight={'bold'}>
          Historical stage progress
        </Typography>
        <Typography textAlign={'left'} mt={1} mb={2}>
          If any historical progress is missing please make sure to add it using the table insert
          row functionality
        </Typography>

        {!isTargetStageDateValid && (
          <Box mb={2} display={'flex'} gap={1}>
            <WarningAmber sx={{ color: FORTA_ERROR }} />
            <Typography textAlign={'left'} fontWeight={'bold'} color={FORTA_ERROR}>
              The migration did not include the stage date; please update the date.
            </Typography>
          </Box>
        )}

        <TableContainer
          sx={{
            borderRadius: '16px',
            border: `1px solid ${FORTA_GRAY1}`,
          }}
          data-testid="targetStageChangeContainer"
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell width={50}></StyledTableCell>
                <StyledTableCell width={'15%'} sx={{ pr: 1 }}>
                  Date
                </StyledTableCell>
                {stages.map((stage) => (
                  <StyledTableCell width={'12%'} key={stage.value}>
                    {stage.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell>Reason for change</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stageHistory.length > 0 ? (
                stageHistory
                  //Edge case after migration: Filter out the on hold stage if the date is null
                  .filter(
                    (entry) =>
                      !(
                        stageHistory.length === 1 &&
                        entry.stage === TargetStages.OnHold &&
                        !entry.date
                      ),
                  )
                  .map((entry, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: index % 2 === 0 ? FORTA_GRAY1 : FORTA_WHITE,
                      }}
                    >
                      <StyledTableCell>
                        {isTpEditable && entry.dateEditsBlocked === null && (
                          <IconButton
                            sx={{
                              padding: 1,
                              opacity: index !== stageHistory.length - 1 ? 0.3 : 1,
                            }}
                            onClick={() => setShowDeleteStageConfirmation(true)}
                            disabled={index !== stageHistory.length - 1}
                            data-testid={`trashicon_${entry.reasonForChange}_${entry.stage.replace(
                              /\s+/g,
                              '_',
                            )}_${target.label}`}
                          >
                            <TrashIcon3 width={20} height={20} />
                          </IconButton>
                        )}
                      </StyledTableCell>
                      {entry.date ? (
                        <StyledTableCell>{moment(entry.date).format('MM/DD/YY')}</StyledTableCell>
                      ) : (
                        <FHAntdDateTimePicker
                          key={target.id}
                          format="MM/DD/YY"
                          allowClear={true}
                          iconWidth={18}
                          iconHeight={18}
                          inputHeight={40}
                          style={{
                            width: '100%',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                          }}
                          disabled={!isTpEditable}
                          disabledDate={disabledFutureDate}
                          value={
                            target?.id
                              ? updateStageDates[target.id]
                                ? moment(updateStageDates[target.id])
                                : null
                              : null
                          }
                          onChange={(value: any) =>
                            target?.id && handleUpdateDateForTarget(target.id, value)
                          }
                          error={target?.id ? !updateStageDates[target.id] : false}
                        />
                      )}
                      {stages.map((stageColumn) => (
                        <StyledTableCell key={stageColumn.value}>
                          {/* Initial stage dot */}
                          {entry.status === TargetStageStatus.Initial &&
                            stageColumn.value === entry.stage && (
                              <StageIndicator color={STATUS_COLORS[TargetStageStatus.Initial]} />
                            )}
                          {/* Stage transitions */}
                          {(entry.status === TargetStageStatus.Progress ||
                            entry.status === TargetStageStatus.Regress) &&
                            entry.transition &&
                            stageColumn.value === entry.transition.from && (
                              <>
                                <StageIndicator
                                  color={
                                    entry.status === TargetStageStatus.Progress
                                      ? STATUS_COLORS[TargetStageStatus.Progress]
                                      : STATUS_COLORS[TargetStageStatus.Regress]
                                  }
                                />
                                <StageArrow
                                  color={
                                    entry.status === TargetStageStatus.Progress
                                      ? STATUS_COLORS[TargetStageStatus.Progress]
                                      : STATUS_COLORS[TargetStageStatus.Regress]
                                  }
                                  direction={entry.transition.direction}
                                  stageCount={entry.transition.stageCount}
                                />
                              </>
                            )}

                          {/* Status Indicator for Hold, and Discontinued*/}
                          {(entry.status === TargetStageStatus.Hold ||
                            entry.status === TargetStageStatus.Resume ||
                            entry.status === TargetStageStatus.Discontinued) &&
                            stageColumn.value === entry.stage && (
                              <StageIndicator
                                color={STATUS_COLORS[entry.status]}
                              />
                            )}
                        </StyledTableCell>
                      ))}
                      <StyledTableCell>{entry.reasonForChange ?? 'N/A'}</StyledTableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={10}>
                    <Typography textAlign="center" fontWeight={500} fontSize={16} my={2}>
                      No stage changes available for this target
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {isTpEditable && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 4,
            backgroundColor: FORTA_BLUE.light3,
            px: 2,
            py: 3,
            borderRadius: '16px',
            boxShadow: `0px 4px 4px rgba(0, 0, 0, 0.25)`,
          }}
        >
          <Typography fontWeight={700} fontSize={21} textAlign={'left'}>
            Change target stage
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography fontWeight="bold" align="left" mb={1}>
                  Date
                </Typography>
                {isTpEditable ? (
                  <FHAntdDateTimePicker
                    data-testid={`stageChangeTargetDatePicker_${target.label}`}
                    format="MM/DD/YY"
                    allowClear={false}
                    inputHeight={40}
                    style={{ width: '100%' }}
                    disabled={!isTpEditable || !isTargetStageDateValid}
                    disabledDate={disabledFutureDate}
                    value={targetStageDate ? moment(targetStageDate) : null}
                    onChange={(value: any) =>
                      setTargetStageDate(value ? moment(value).format('YYYY-MM-DD') : null)
                    }
                    error={false}
                  />
                ) : (
                  <Typography align="left">
                    {targetStageDate ? moment(targetStageDate).format('MM/DD/YY') : 'N/A'}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography fontWeight="bold" align="left" mb={1}>
                  Stage
                </Typography>
                {isTpEditable ? (
                  <FHSelect
                    data-testid={`updateStageDropdown_${target.label}`}
                    sx={{
                      width: '100%',
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                      },
                    }}
                    inputHeight={40}
                    fullWidth
                    displayEmpty
                    disabled={!isTpEditable || !isTargetStageDateValid}
                    renderValue={(value) => (
                      <Typography sx={{ color: value ? 'inherit' : FORTA_GRAY2 }}>
                        {value
                          ? getTargetStagesForGoalType(goalType).find(
                              (stage) => stage.value === value,
                            )?.label
                          : 'Select stage'}
                      </Typography>
                    )}
                    options={getAvailableStages(targetData?.stage_change_logs ?? [], goalType)}
                    optionDataTestIdPrefix="updateStageOption"
                    value={targetStage || null}
                    onUpdate={(value) =>
                      setTargetStage(isTargetStage(value) ? value : null)
                    }
                    error={false}
                  />
                ) : (
                  <Typography align="left">Selected Stage</Typography>
                )}
              </Box>
            </Grid>
            {/* Reason for change only required when selected stage is on hold, or discontinued */}
            {targetStage &&
              (targetStage === TargetStages.OnHold ||
                targetStage === TargetStages.Discontinued) && (
                <Grid item xs={4.5}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography fontWeight="bold" align="left" mb={1}>
                      Reason for change
                    </Typography>
                    <FHInputField
                      inputHeight={40}
                      fullWidth
                      data-testid={`reasonForChangeTextField_${target.label}`}
                      disabled={!isTpEditable || !isTargetStageDateValid}
                      value={targetStageReason || ''}
                      onChange={(event: any) => setTargetStageReason(event.target.value)}
                      error={!targetStageReason}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {targetStage === TargetStages.OnHold
                              ? 'On Hold - '
                              : targetStage === TargetStages.Discontinued
                                ? 'Discontinued - '
                                : undefined}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
              )}

            <Grid item sx={{ ml: 'auto', alignSelf: 'flex-end', mb: 0.5 }}>
              <FHButton
                onClick={handleStageChange}
                startIcon={<ChangeStageIcon />}
                disabled={
                  !targetStageDate ||
                  !targetStage ||
                  ((targetStage === TargetStages.OnHold ||
                    targetStage === TargetStages.Discontinued) &&
                    !targetStageReason)
                }
                data-testid="changeStageButton"
              >
                Change Stage
              </FHButton>
            </Grid>
          </Grid>
          {(targetStage === TargetStages.Discontinued || targetStage === TargetStages.OnHold) && (
            <Typography textAlign={'left'} color={FORTA_ERROR}>
              Once a target is in the Discontinued or On Hold stage, it will be not be available for
              practice during the session.
            </Typography>
          )}
        </Box>
      )}
      <FHConfirmDialog
        open={showDeleteStageConfirmation}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          width: '650px',
          maxWidth: '650px',
        }}
        title={'Delete target stage'}
        contentRenderer={() => (
          <Box
            sx={{
              height: '150px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography lineHeight={1.5} variant="h4" mb={1}>
              Are you sure you want to delete this target stage?
            </Typography>
            <Typography lineHeight={1.5} variant="h4">
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext="Delete"
        submitBtnTestId="confirmDeleteBtn"
        canceltext="Cancel"
        cancelBtnTestId="cancel"
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          setShowDeleteStageConfirmation(false);
        }}
        onSubmit={handleDeleteStage}
      />
    </Grid>
  );
}

export default TargetStageChange;
